import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Facials = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'HydraFacial (Signature)',
      price: '$150',
      unit: 'per session'
    },
    {
      title: 'HydraFacial (Deluxe)',
      price: '$250',
      unit: 'per session'
    },
    {
      title: 'HydraFacial (Platinum)',
      price: '$300',
      unit: 'per session'
    },
    {
      title: 'Moveo Glo Facial - Laser Facial',
      price: '$250',
      unit: 'per session'
    }
  ];

  const benefits = [
    {
      title: 'Deep Cleansing & Exfoliation',
      description: 'Thoroughly cleanses pores while gently exfoliating dead skin cells for a refreshed, renewed complexion.'
    },
    {
      title: 'Intense Hydration',
      description: 'Delivers deep moisture and nourishment to plump skin, reduce fine lines, and restore radiance.'
    },
    {
      title: 'Immediate Visible Results',
      description: 'See instant improvements in skin clarity, brightness, and texture right after your first treatment.'
    },
    {
      title: 'Customizable Treatments',
      description: 'Tailored to your specific skin concerns including aging, acne, pigmentation, and sensitivity.'
    },
    {
      title: 'No Downtime Required',
      description: 'Non-invasive procedures with zero recovery time - resume normal activities immediately after treatment.'
    },
    {
      title: 'Suitable for All Skin Types',
      description: 'Safe and effective for all skin types and tones, including sensitive and problematic skin.'
    },
    {
      title: 'Reduces Fine Lines & Wrinkles',
      description: 'Smooths fine lines and softens wrinkles through deep hydration and collagen stimulation.'
    },
    {
      title: 'Improves Skin Texture & Tone',
      description: 'Refines rough texture and evens out skin tone for a smoother, more polished appearance.'
    },
    {
      title: 'Minimizes Pores',
      description: 'Deep cleansing reduces the appearance of enlarged pores for refined, clearer-looking skin.'
    },
    {
      title: 'Addresses Multiple Concerns',
      description: 'Treats various issues including congestion, dullness, uneven tone, acne, and sun damage in one session.'
    },
    {
      title: 'Enhances Product Absorption',
      description: 'Clean, exfoliated skin allows serums and skincare products to penetrate deeper and work more effectively.'
    },
    {
      title: 'Long-Lasting Radiance',
      description: 'Regular treatments maintain healthy, glowing skin with cumulative benefits over time.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>HydraFacial & Laser Facials in Forest Hills | Century Aesthetics</title>
        <meta name="description" content="Advanced facial treatments including HydraFacial and Moveo Glo Laser Facials for radiant, healthy skin in Forest Hills, NY." />
        <meta name="keywords" content="hydrafacial, laser facial, facial treatment, skin rejuvenation, moveo glo, facial forest hills" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/facials" />
        
        <meta property="og:title" content="HydraFacial & Laser Facials | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Advanced facial treatments including HydraFacial and Moveo Glo Laser Facials for radiant, healthy skin in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/facials" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/hydra.png" 
                  alt="Facial Treatments - HydraFacial & Moveo Glo" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                PROFESSIONAL FACIAL TREATMENTS: HYDRAFACIAL & LASER FACIALS
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our comprehensive facial treatments combine the best of HydraFacial technology and advanced laser facials to address all your skincare needs. HydraFacial uses patented vortex technology to deeply cleanse, exfoliate, and hydrate skin while extracting impurities and infusing nourishing serums. Available in Signature, Deluxe, and Platinum options, each treatment level offers increasing benefits and customization. The Moveo Glo Laser Facial uses controlled light energy to stimulate collagen production, reduce fine lines, improve skin texture, and address pigmentation concerns for comprehensive facial rejuvenation.
                </p>
                <p>
                  All facial treatments are non-invasive with no downtime, suitable for all skin types, and deliver immediate visible results. HydraFacial provides instant improvements in skin clarity and hydration, while laser facials offer progressive collagen-building benefits over time. Regular treatments every 4-6 weeks maintain optimal skin health, enhance product absorption, and keep your complexion glowing. Schedule your facial treatment at Century Longevity & Aesthetics to experience professional skincare that delivers real, lasting results.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Facial Treatment Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Consultation for All Facial Services
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-lg p-8 shadow-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-lg font-heading font-semibold text-olive-darkest mb-3 min-h-[3rem] flex items-center justify-center">
                  {option.title}
                </h3>
                <p className="text-5xl font-heading font-light text-gold mb-1">
                  {option.price}
                </p>
                <p className="text-sm font-body text-gray-600 mb-6">
                  {option.unit}
                </p>
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Professional Facial Treatments</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Experience the transformative power of professional facial treatments. Reveal clearer, more radiant, youthful-looking skin!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about facial treatments, what to expect during your session, and which option is best for your skin concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('facials');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Facials;